/**
 * 💰 AFFILIATE DASHBOARD - Tableau de bord Affiliation MLM
 * 
 * Programme d'affiliation Heartbeats Premium à 4 niveaux:
 * - N1: 2% (0,20€) - Filleul direct
 * - N2: 1% (0,10€)
 * - N3: 0.5% (0,05€)
 * - N4: 0.25% (0,025€)
 * TOTAL: 3.75% (0,37€)
 * 
 * Fonctionnalités:
 * - Statistiques en temps réel
 * - Arbre de réseau visuel
 * - Historique des commissions
 * - Demande de retrait (IBAN, PayPal, Crédit app)
 * - Paiement automatique PayPal
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, TrendingUp, Wallet, Copy, Share2, ChevronRight,
  Gift, Clock, CheckCircle, AlertCircle, CreditCard, Building2,
  Network, ArrowUpRight, Sparkles, Crown, Eye, EyeOff, Image
} from 'lucide-react';
import AffiliateWidgets from './AffiliateWidgets';

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration des commissions - NOUVEAU PLAN SÉCURISÉ
const COMMISSION_CONFIG = {
  N1: { percent: 2, amount: 0.20, label: 'Filleul direct', color: 'from-emerald-500 to-green-500' },
  N2: { percent: 1, amount: 0.10, label: 'Niveau 2', color: 'from-blue-500 to-cyan-500' },
  N3: { percent: 0.5, amount: 0.05, label: 'Niveau 3', color: 'from-purple-500 to-violet-500' },
  N4: { percent: 0.25, amount: 0.025, label: 'Niveau 4', color: 'from-pink-500 to-rose-500' },
};

/**
 * 💰 Dashboard Principal d'Affiliation
 */
export function AffiliateDashboard({ customerId, customerName }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showWidgets, setShowWidgets] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/affiliation/dashboard/${customerId}`);
      const data = await res.json();
      
      if (data.success) {
        setDashboardData(data);
      } else {
        setError(data.message || 'Erreur de chargement');
      }
    } catch (err) {
      setError('Impossible de charger le dashboard');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) {
      fetchDashboard();
    }
  }, [customerId, fetchDashboard]);

  const copyCode = () => {
    if (dashboardData?.affiliate?.code) {
      navigator.clipboard.writeText(dashboardData.affiliate.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = () => {
    const code = dashboardData?.affiliate?.code;
    if (code) {
      const shareUrl = `${window.location.origin}?ref=${code}`;
      if (navigator.share) {
        navigator.share({
          title: 'Rejoins Heartbeats !',
          text: `Utilise mon code ${code} pour -15 jours gratuits sur Heartbeats Premium !`,
          url: shareUrl
        });
      } else {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!dashboardData?.isAffiliate) {
    return (
      <AffiliateRegistration 
        customerId={customerId} 
        customerName={customerName}
        onSuccess={fetchDashboard}
      />
    );
  }

  const { affiliate, stats, recentCommissions, directReferrals } = dashboardData;

  return (
    <div className="space-y-6" data-testid="affiliate-dashboard">
      {/* Header avec code */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 p-6 text-white">
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Crown className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Programme Affiliation</h2>
                <p className="text-white/80 text-sm">Gagnez des commissions récurrentes</p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-bold ${
              affiliate.status === 'active' ? 'bg-green-500/30 text-green-100' : 'bg-red-500/30 text-red-100'
            }`}>
              {affiliate.status === 'active' ? '✓ Actif' : '⚠ ' + affiliate.status}
            </div>
          </div>
          
          {/* LIEN d'affiliation MODERNE */}
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
            <p className="text-white/70 text-xs mb-2">🔗 Votre lien d'affiliation</p>
            <p className="text-sm font-mono bg-black/30 p-2 rounded-lg break-all mb-3">
              https://heartbeats.fr/?ref={affiliate.code}
            </p>
            <div className="flex gap-2">
              <button
                onClick={shareLink}
                className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 transition-colors text-sm font-medium"
                data-testid="share-affiliate-link"
              >
                <Share2 className="w-4 h-4" />
                Partager
              </button>
              <button
                onClick={copyCode}
                className="p-3 rounded-xl bg-white/20 hover:bg-white/30 transition-colors"
                data-testid="copy-affiliate-code"
              >
                {copied ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-white/50 text-xs mt-2 text-center">
              Tracking automatique • Cookie 90 jours
            </p>
            
            {/* Bouton Widgets & Bannières */}
            <button
              onClick={() => setShowWidgets(true)}
              className="w-full mt-3 flex items-center justify-center gap-2 p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-sm"
            >
              <Image className="w-4 h-4" />
              Widgets & Bannières
            </button>
          </div>
        </div>
        
        {/* Particules décoratives */}
        <div className="absolute top-4 right-4 w-20 h-20 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-4 left-4 w-16 h-16 bg-pink-300/20 rounded-full blur-xl" />
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Réseau total"
          value={Object.values(stats.total_referrals || {}).reduce((a, b) => a + b, 0)}
          color="from-blue-500 to-cyan-500"
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Ce mois"
          value={`${stats.thisMonthEarnings?.toFixed(2) || '0.00'}€`}
          color="from-emerald-500 to-green-500"
        />
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          label="En attente"
          value={`${stats.pending_balance?.toFixed(2) || '0.00'}€`}
          color="from-amber-500 to-orange-500"
        />
        <StatCard
          icon={<Wallet className="w-5 h-5" />}
          label="Disponible"
          value={`${stats.available_balance?.toFixed(2) || '0.00'}€`}
          color="from-purple-500 to-pink-500"
          onClick={() => stats.available_balance >= 50 && setShowWithdrawModal(true)}
          clickable={stats.available_balance >= 50}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: 'Aperçu', icon: <TrendingUp className="w-4 h-4" /> },
          { id: 'network', label: 'Réseau', icon: <Network className="w-4 h-4" /> },
          { id: 'commissions', label: 'Commissions', icon: <Gift className="w-4 h-4" /> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu des tabs */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Commission rates */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-500" />
                Vos taux de commission
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(COMMISSION_CONFIG).map(([level, config]) => (
                  <div
                    key={level}
                    className={`bg-gradient-to-br ${config.color} rounded-xl p-4 text-white`}
                  >
                    <p className="text-white/80 text-xs">{config.label}</p>
                    <p className="text-2xl font-bold">{config.percent}%</p>
                    <p className="text-sm text-white/90">{config.amount}€/mois</p>
                    <p className="text-xs text-white/70 mt-1">
                      {stats.total_referrals?.[level] || 0} actifs
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Filleuls directs récents */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Filleuls directs récents
              </h3>
              {directReferrals?.length > 0 ? (
                <div className="space-y-2">
                  {directReferrals.slice(0, 5).map((ref, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {ref.name?.[0] || '?'}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{ref.name}</p>
                        <p className="text-xs text-gray-500">
                          Inscrit le {new Date(ref.joinedAt).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        ref.isActive 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {ref.isActive ? '● Actif' : '○ Inactif'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Aucun filleul pour le moment</p>
                  <p className="text-sm">Partagez votre code pour commencer !</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'network' && (
          <motion.div
            key="network"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <NetworkTree affiliateId={affiliate.id} stats={stats} />
          </motion.div>
        )}

        {activeTab === 'commissions' && (
          <motion.div
            key="commissions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <CommissionHistory commissions={recentCommissions} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal retrait */}
      <AnimatePresence>
        {showWithdrawModal && (
          <WithdrawModal
            affiliateId={affiliate.id}
            availableBalance={stats.available_balance}
            iban={affiliate.iban}
            paypalEmail={dashboardData?.affiliate?.paypal_email}
            paypalVerified={dashboardData?.affiliate?.paypal_email_verified}
            onClose={() => setShowWithdrawModal(false)}
            onSuccess={fetchDashboard}
          />
        )}
      </AnimatePresence>
      
      {/* Modal Widgets & Bannières */}
      <AnimatePresence>
        {showWidgets && (
          <AffiliateWidgets 
            affiliateCode={affiliate.code} 
            onClose={() => setShowWidgets(false)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * 📊 Carte de statistique
 */
function StatCard({ icon, label, value, color, onClick, clickable }) {
  return (
    <motion.div
      whileHover={clickable ? { scale: 1.02 } : {}}
      whileTap={clickable ? { scale: 0.98 } : {}}
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm ${
        clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
      }`}
    >
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      {clickable && (
        <p className="text-xs text-pink-500 mt-1 flex items-center gap-1">
          Retirer <ArrowUpRight className="w-3 h-3" />
        </p>
      )}
    </motion.div>
  );
}

/**
 * 📝 Formulaire d'inscription affilié
 */
function AffiliateRegistration({ customerId, customerName, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLegalTerms, setShowLegalTerms] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    firstName: customerName?.split(' ')[0] || '',
    lastName: customerName?.split(' ').slice(1).join(' ') || '',
    iban: '',
    paypalEmail: '',
    acceptLegalTerms: false,
    autoPayoutEnabled: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptLegalTerms) {
      setError('Vous devez accepter les conditions du programme');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/api/affiliation/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customerId,
          phone: formData.phone,
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          iban: formData.iban || null,
          paypal_email: formData.paypalEmail || null,
          accept_legal_terms: formData.acceptLegalTerms,
          auto_payout_enabled: formData.autoPayoutEnabled
        })
      });

      const data = await res.json();

      if (data.success) {
        onSuccess();
      } else {
        setError(data.detail || data.message || 'Erreur d\'inscription');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm" data-testid="affiliate-registration">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
          <Gift className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Rejoignez le Programme d'Affiliation
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          Gagnez jusqu'à 3,75% de commissions récurrentes sur 4 niveaux !
        </p>
      </div>

      {/* Avantages */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {[
          { icon: '💰', text: 'N1: 2% (0,20€/mois)' },
          { icon: '📈', text: 'N2: 1% (0,10€/mois)' },
          { icon: '🎯', text: 'N3: 0,5% (0,05€/mois)' },
          { icon: '🌟', text: 'N4: 0,25% (0,025€/mois)' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
            <span className="text-xl">{item.icon}</span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.text}</span>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Prénom
            </label>
            <input
              type="text"
              required
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              data-testid="affiliate-firstname"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nom
            </label>
            <input
              type="text"
              required
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              data-testid="affiliate-lastname"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Téléphone
          </label>
          <input
            type="tel"
            required
            placeholder="06 12 34 56 78"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            data-testid="affiliate-phone"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email
          </label>
          <input
            type="email"
            required
            placeholder="votre@email.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            data-testid="affiliate-email"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email PayPal (optionnel - paiement automatique)
          </label>
          <input
            type="email"
            placeholder="paypal@email.com"
            value={formData.paypalEmail}
            onChange={(e) => setFormData({ ...formData, paypalEmail: e.target.value })}
            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            data-testid="affiliate-paypal"
          />
          <p className="text-xs text-gray-500 mt-1">Vous devrez vérifier cet email pour recevoir les paiements</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            IBAN (optionnel - pour les virements)
          </label>
          <input
            type="text"
            placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
            value={formData.iban}
            onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            data-testid="affiliate-iban"
          />
        </div>

        {/* Paiement automatique */}
        <div className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.autoPayoutEnabled}
              onChange={(e) => setFormData({ ...formData, autoPayoutEnabled: e.target.checked })}
              className="w-5 h-5 mt-0.5 rounded border-gray-300 text-green-500 focus:ring-green-500"
            />
            <div>
              <span className="font-medium text-gray-900 dark:text-white">💸 Paiement automatique</span>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Vos gains seront versés automatiquement le 1er du mois dès que vous atteignez 50€
              </p>
            </div>
          </label>
        </div>

        {/* Conditions légales */}
        <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.acceptLegalTerms}
              onChange={(e) => setFormData({ ...formData, acceptLegalTerms: e.target.checked })}
              className="w-5 h-5 mt-0.5 rounded border-gray-300 text-pink-500 focus:ring-pink-500"
              data-testid="affiliate-accept-terms"
            />
            <div>
              <span className="font-medium text-gray-900 dark:text-white">J'accepte les conditions du programme</span>
              <button
                type="button"
                onClick={() => setShowLegalTerms(!showLegalTerms)}
                className="text-pink-500 hover:text-pink-600 text-sm ml-1 underline"
              >
                (voir les conditions)
              </button>
            </div>
          </label>
          
          {/* Conditions dépliables */}
          {showLegalTerms && (
            <div className="mt-3 p-3 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-400 space-y-2">
              <p className="font-bold text-gray-900 dark:text-white">En vous inscrivant, vous acceptez :</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>D'être seul responsable de votre conformité avec le RGPD</li>
                <li>De clairement identifier le contenu sponsorisé (#pub, #partenariat)</li>
                <li>De ne pas faire de promesses de gains irréalistes</li>
                <li>De respecter les règles des plateformes (Instagram, TikTok, etc.)</li>
                <li>De déclarer vos revenus conformément à la législation fiscale</li>
                <li>Que Heartbeats peut suspendre votre compte en cas de non-conformité</li>
              </ul>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !formData.acceptLegalTerms}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2"
          data-testid="affiliate-submit"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Rejoindre le programme
            </>
          )}
        </button>
      </form>
    </div>
  );
}

/**
 * 🌳 Arbre du réseau
 */
function NetworkTree({ affiliateId, stats }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTree = async () => {
      try {
        const res = await fetch(`${API}/api/affiliation/network-tree/${affiliateId}`);
        const data = await res.json();
        if (data.success) {
          setTree(data);
        }
      } catch (err) {
        console.error('Erreur chargement arbre:', err);
      } finally {
        setLoading(false);
      }
    };

    if (affiliateId) {
      fetchTree();
    }
  }, [affiliateId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm">
      <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <Network className="w-5 h-5 text-purple-500" />
        Votre réseau par niveau
      </h3>

      {/* Stats par niveau */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        {Object.entries(COMMISSION_CONFIG).map(([level, config]) => (
          <div
            key={level}
            className="text-center p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50"
          >
            <p className={`text-2xl font-bold bg-gradient-to-r ${config.color} bg-clip-text text-transparent`}>
              {tree?.counts?.[level] || stats.total_referrals?.[level] || 0}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{level}</p>
          </div>
        ))}
      </div>

      {/* Arbre visuel */}
      {tree?.tree?.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {tree.tree.map((node, i) => (
            <NetworkNode key={i} node={node} level={1} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Network className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Votre réseau est vide</p>
          <p className="text-sm">Partagez votre code pour le développer !</p>
        </div>
      )}

      {/* Total */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <span className="text-gray-500 dark:text-gray-400">Total réseau</span>
        <span className="text-xl font-bold text-gray-900 dark:text-white">
          {tree?.totalNetwork || Object.values(stats.total_referrals || {}).reduce((a, b) => a + b, 0)} personnes
        </span>
      </div>
    </div>
  );
}

/**
 * 🌿 Noeud de l'arbre réseau
 */
function NetworkNode({ node, level }) {
  const [expanded, setExpanded] = useState(level <= 2);
  const hasChildren = node.children?.length > 0;
  const levelConfig = COMMISSION_CONFIG[`N${level}`];

  return (
    <div className="pl-4 border-l-2 border-gray-200 dark:border-gray-700">
      <div
        className={`flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
          hasChildren ? 'cursor-pointer' : ''
        }`}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren && (
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        )}
        {!hasChildren && <div className="w-4" />}
        
        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${levelConfig?.color || 'from-gray-400 to-gray-500'} flex items-center justify-center text-white text-sm font-bold`}>
          {node.name?.[0] || '?'}
        </div>
        
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-white text-sm">{node.name}</p>
          <p className="text-xs text-gray-500">N{level}</p>
        </div>
        
        <span className={`px-2 py-0.5 rounded-full text-xs ${
          node.isActive 
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
        }`}>
          {node.isActive ? 'Actif' : 'Inactif'}
        </span>
      </div>

      {/* Enfants */}
      <AnimatePresence>
        {expanded && hasChildren && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            {node.children.map((child, i) => (
              <NetworkNode key={i} node={child} level={level + 1} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * 📜 Historique des commissions
 */
function CommissionHistory({ commissions }) {
  const [showDetails, setShowDetails] = useState(null);

  const statusConfig = {
    pending: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: <Clock className="w-3 h-3" />, label: 'En attente' },
    validated: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: <CheckCircle className="w-3 h-3" />, label: 'Validée' },
    paid: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: <Wallet className="w-3 h-3" />, label: 'Payée' },
    cancelled: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: <AlertCircle className="w-3 h-3" />, label: 'Annulée' },
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm">
      <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <Gift className="w-5 h-5 text-green-500" />
        Historique des commissions
      </h3>

      {commissions?.length > 0 ? (
        <div className="space-y-2">
          {commissions.map((com, i) => {
            const status = statusConfig[com.status] || statusConfig.pending;
            const levelConfig = COMMISSION_CONFIG[com.level];
            
            return (
              <div
                key={i}
                className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={() => setShowDetails(showDetails === i ? null : i)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${levelConfig?.color || 'from-gray-400 to-gray-500'} flex items-center justify-center text-white text-xs font-bold`}>
                      {com.level}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        +{com.commission_amount?.toFixed(2)}€
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(com.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                    {status.icon}
                    {status.label}
                  </span>
                </div>

                <AnimatePresence>
                  {showDetails === i && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400"
                    >
                      <p>Taux: {com.rate}%</p>
                      <p>Base: {com.base_amount?.toFixed(2)}€</p>
                      {com.validation_date && (
                        <p>Validation: {new Date(com.validation_date).toLocaleDateString('fr-FR')}</p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Gift className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Aucune commission pour le moment</p>
          <p className="text-sm">Elles apparaîtront ici dès que vos filleuls s'abonneront</p>
        </div>
      )}
    </div>
  );
}

/**
 * 💸 Modal de retrait
 */
function WithdrawModal({ affiliateId, availableBalance, iban, paypalEmail, paypalVerified, onClose, onSuccess }) {
  const [amount, setAmount] = useState(Math.min(availableBalance, 50));
  const [method, setMethod] = useState(paypalVerified ? 'paypal' : iban ? 'iban' : 'credit');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // PayPal verification state
  const [showPaypalSetup, setShowPaypalSetup] = useState(false);
  const [newPaypalEmail, setNewPaypalEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [paypalStep, setPaypalStep] = useState('email'); // 'email' | 'verify'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/api/affiliation/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          affiliate_id: affiliateId,
          amount: parseFloat(amount),
          method,
          paypal_email: method === 'paypal' ? paypalEmail : null
        })
      });

      const data = await res.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(data.detail || data.message || 'Erreur de retrait');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handlePaypalSetup = async () => {
    if (paypalStep === 'email') {
      // Envoyer le code de vérification
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/api/affiliation/update-paypal/${affiliateId}?email=${encodeURIComponent(newPaypalEmail)}`, {
          method: 'POST'
        });
        const data = await res.json();
        if (data.success) {
          setPaypalStep('verify');
        } else {
          setError(data.detail || 'Erreur d\'envoi');
        }
      } catch (err) {
        setError('Erreur de connexion');
      } finally {
        setLoading(false);
      }
    } else {
      // Vérifier le code
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/api/affiliation/verify-paypal/${affiliateId}?code=${verificationCode}`, {
          method: 'POST'
        });
        const data = await res.json();
        if (data.success) {
          setShowPaypalSetup(false);
          setMethod('paypal');
          onSuccess(); // Rafraîchir les données
        } else {
          setError(data.detail || 'Code incorrect');
        }
      } catch (err) {
        setError('Erreur de connexion');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="withdraw-modal"
      >
        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Demande envoyée !
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Votre retrait sera traité sous 48-72h
            </p>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Wallet className="w-6 h-6 text-purple-500" />
              Demande de retrait
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Montant (min. 50€)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="50"
                    max={availableBalance}
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent text-lg"
                    data-testid="withdraw-amount"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Disponible: {availableBalance?.toFixed(2)}€
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mode de paiement
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setMethod('iban')}
                    disabled={!iban}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      method === 'iban'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    } ${!iban ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Building2 className={`w-6 h-6 ${method === 'iban' ? 'text-purple-500' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${method === 'iban' ? 'text-purple-700 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      Virement
                    </span>
                    {!iban && <span className="text-xs text-gray-400">IBAN requis</span>}
                  </button>
                  <button
                    type="button"
                    onClick={() => paypalVerified ? setMethod('paypal') : setShowPaypalSetup(true)}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      method === 'paypal'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <svg className={`w-6 h-6 ${method === 'paypal' ? 'text-blue-500' : 'text-gray-400'}`} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c.013.076.024.15.034.226.29 2.07-.424 3.476-1.53 4.494-1.19 1.095-2.904 1.707-4.94 1.707H11.89a.645.645 0 0 0-.633.74l-.977 6.187 1.12-7.106c.082-.518.526-.9 1.05-.9h2.19c4.298 0 7.664-1.747 8.647-6.797.03-.149.054-.294.077-.437-.055-.047-.111-.092-.168-.135z"/>
                    </svg>
                    <span className={`text-sm font-medium ${method === 'paypal' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      PayPal
                    </span>
                    {!paypalVerified && <span className="text-xs text-gray-400">Configurer</span>}
                    {paypalVerified && <span className="text-xs text-green-500">✓ Vérifié</span>}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod('credit')}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      method === 'credit'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className={`w-6 h-6 ${method === 'credit' ? 'text-purple-500' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${method === 'credit' ? 'text-purple-700 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      Crédit app
                    </span>
                  </button>
                </div>
              </div>

              {/* PayPal Setup Modal */}
              {showPaypalSetup && (
                <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <h4 className="font-bold text-blue-700 dark:text-blue-400 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c.013.076.024.15.034.226.29 2.07-.424 3.476-1.53 4.494-1.19 1.095-2.904 1.707-4.94 1.707H11.89a.645.645 0 0 0-.633.74l-.977 6.187 1.12-7.106c.082-.518.526-.9 1.05-.9h2.19c4.298 0 7.664-1.747 8.647-6.797.03-.149.054-.294.077-.437-.055-.047-.111-.092-.168-.135z"/>
                    </svg>
                    Configuration PayPal
                  </h4>
                  
                  {paypalStep === 'email' ? (
                    <div className="space-y-3">
                      <p className="text-sm text-blue-600 dark:text-blue-300">
                        Entrez votre email PayPal. Un code de vérification vous sera envoyé.
                      </p>
                      <input
                        type="email"
                        placeholder="votre@email.com"
                        value={newPaypalEmail}
                        onChange={(e) => setNewPaypalEmail(e.target.value)}
                        className="w-full px-4 py-2 rounded-xl border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setShowPaypalSetup(false)}
                          className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm"
                        >
                          Annuler
                        </button>
                        <button
                          type="button"
                          onClick={handlePaypalSetup}
                          disabled={!newPaypalEmail || loading}
                          className="flex-1 py-2 rounded-xl bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
                        >
                          {loading ? 'Envoi...' : 'Envoyer le code'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-blue-600 dark:text-blue-300">
                        Un code a été envoyé à <strong>{newPaypalEmail}</strong>. Entrez-le ci-dessous.
                      </p>
                      <input
                        type="text"
                        placeholder="Code à 6 chiffres"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        maxLength={6}
                        className="w-full px-4 py-2 rounded-xl border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => { setPaypalStep('email'); setVerificationCode(''); }}
                          className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm"
                        >
                          Retour
                        </button>
                        <button
                          type="button"
                          onClick={handlePaypalSetup}
                          disabled={verificationCode.length !== 6 || loading}
                          className="flex-1 py-2 rounded-xl bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
                        >
                          {loading ? 'Vérification...' : 'Vérifier'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading || amount < 50 || amount > availableBalance}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2"
                  data-testid="withdraw-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <ArrowUpRight className="w-5 h-5" />
                      Retirer
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

export default AffiliateDashboard;
